/**
 * Metrics — přehled časových řad, stav nodů, Grafana integrace
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyState,
  PageHeader,
  RefreshButton,
  StatCard,
  StatsGrid,
  Tab,
  TabList,
  TabPanel,
  Tabs,
} from '../../components/ui';
import { Activity, BarChart2, Server, Wifi } from 'lucide-react';
import { mcpCall } from '../../lib/mcp';

// ── Types ─────────────────────────────────────────────────────────────────────

interface NodeRow {
  host: string;
  measurement: string;
  tags: Record<string, string>;
  fields: Record<string, number | string | boolean>;
  ts: string;
}

interface NodesResponse {
  ok: boolean;
  nodes: NodeRow[];
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtVal(v: number | string | boolean): string {
  if (typeof v === 'boolean') return v ? 'true' : 'false';
  if (typeof v === 'number') return v % 1 === 0 ? String(v) : v.toFixed(2);
  return String(v);
}

function fmtAge(ts: string): string {
  const sec = Math.floor((Date.now() - new Date(ts).getTime()) / 1000);
  if (sec < 60) return `${sec}s`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m`;
  return `${Math.floor(sec / 3600)}h`;
}

function freshness(ts: string): 'green' | 'yellow' | 'red' {
  const sec = (Date.now() - new Date(ts).getTime()) / 1000;
  if (sec < 120) return 'green';
  if (sec < 600) return 'yellow';
  return 'red';
}

// ── Node card ─────────────────────────────────────────────────────────────────

function NodeCard({ row }: { row: NodeRow }) {
  const fields = Object.entries(row.fields).filter(([, v]) => typeof v === 'number');

  return (
    <div className='border border-gray-700 rounded-xl px-4 py-3 space-y-2'>
      <div className='flex items-center gap-3'>
        <Server size={14} className='text-gray-500 shrink-0' />
        <span className='font-mono text-sm text-white flex-1'>{row.host}</span>
        <Badge variant='gray' className='font-mono text-xs'>{row.measurement}</Badge>
        <Badge variant={freshness(row.ts)}>{fmtAge(row.ts)}</Badge>
      </div>
      {fields.length > 0 && (
        <div className='flex flex-wrap gap-x-4 gap-y-1 pl-5'>
          {fields.map(([k, v]) => (
            <span key={k} className='text-xs text-gray-400'>
              <span className='text-gray-500'>{k}:</span>{' '}
              <span className='text-gray-200 font-mono'>{fmtVal(v as number)}</span>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Grafana setup tab ─────────────────────────────────────────────────────────

function GrafanaSetup() {
  const { t } = useTranslation();
  const origin = window.location.origin;

  return (
    <div className='space-y-4 max-w-2xl mt-4'>
      <Card>
        <CardHeader>
          <CardTitle icon={BarChart2}>Grafana — Data Source</CardTitle>
        </CardHeader>
        <CardContent>
          <p className='text-sm text-gray-400 mb-4'>
            {t('metrics.grafanaIntro')}
          </p>
          <div className='space-y-3 text-sm'>
            <div className='flex gap-3'>
              <span className='text-gray-500 w-24 shrink-0'>Type</span>
              <code className='text-blue-300'>Prometheus</code>
            </div>
            <div className='flex gap-3'>
              <span className='text-gray-500 w-24 shrink-0'>URL</span>
              <code className='text-blue-300 break-all'>{origin}/apiext/metrics</code>
            </div>
            <div className='flex gap-3'>
              <span className='text-gray-500 w-24 shrink-0'>Auth</span>
              <code className='text-blue-300'>
                {t('metrics.grafanaAuthValue')}
              </code>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle icon={Wifi}>Proxmox — Metric Server</CardTitle>
        </CardHeader>
        <CardContent>
          <p className='text-sm text-gray-400 mb-4'>
            Datacenter → Metric Server → Add → InfluxDB
          </p>
          <div className='space-y-3 text-sm'>
            <div className='flex gap-3'>
              <span className='text-gray-500 w-24 shrink-0'>Type</span>
              <code className='text-blue-300'>InfluxDB</code>
            </div>
            <div className='flex gap-3'>
              <span className='text-gray-500 w-24 shrink-0'>Host</span>
              <code className='text-blue-300 break-all'>{window.location.hostname}</code>
            </div>
            <div className='flex gap-3'>
              <span className='text-gray-500 w-24 shrink-0'>Port</span>
              <code className='text-blue-300'>{window.location.port || '443'}</code>
            </div>
            <div className='flex gap-3'>
              <span className='text-gray-500 w-24 shrink-0'>Port</span>
              <code className='text-blue-300'>8089 UDP</code>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('metrics.powerScriptTitle')}</CardTitle>
        </CardHeader>
        <CardContent>
          <pre className='text-xs text-gray-300 bg-gray-950 rounded-lg p-3 overflow-x-auto whitespace-pre'>{`# /etc/cron.d/acm-power
* * * * * root W=$(ipmitool dcmi power reading 2>/dev/null | awk '/Instantaneous/{print $NF}'); [ -n "$W" ] && echo "power,host=$(hostname) watts=$W" | nc -u -w1 ${window.location.hostname} 8089`}</pre>
        </CardContent>
      </Card>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function MetricsPage() {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');

  const { data, isLoading, isError, isFetching, refetch } = useQuery<NodesResponse>({
    queryKey: ['metrics-nodes'],
    queryFn: () => mcpCall<NodesResponse>('metrics.nodes', {}, { silent: true }),
    refetchInterval: 30_000,
  });

  const nodes = (data?.nodes ?? []).filter((n) =>
    !search || n.host.includes(search) || n.measurement.includes(search)
  );

  const hosts = new Set(data?.nodes.map((n) => n.host) ?? []).size;
  const fresh = (data?.nodes ?? []).filter((n) => freshness(n.ts) === 'green').length;
  const stale = (data?.nodes ?? []).filter((n) => freshness(n.ts) === 'red').length;

  return (
    <div className='space-y-6'>
      <PageHeader
        title={t('metrics.pageTitle')}
        description={t('metrics.pageDescription')}
        icon={<Activity size={20} className='text-blue-400' />}
        actions={<RefreshButton onRefresh={refetch} loading={isFetching} />}
      />

      <StatsGrid>
        <StatCard label={t('metrics.statHosts')} value={hosts} icon={Server} />
        <StatCard label={t('metrics.statActive')} value={fresh} icon={Activity} variant='success' />
        <StatCard
          label={t('metrics.statNoData')}
          value={stale}
          icon={Activity}
          variant={stale > 0 ? 'warning' : undefined}
        />
      </StatsGrid>

      <Tabs defaultTab='nodes'>
        <TabList>
          <Tab id='nodes' icon={<Server size={14} />}>{t('metrics.tabNodes')}</Tab>
          <Tab id='grafana' icon={<BarChart2 size={14} />}>Grafana</Tab>
        </TabList>

        <TabPanel id='nodes'>
          <Card>
            <CardHeader>
              <CardTitle>{t('metrics.lastValuesTitle')}</CardTitle>
              <input
                className='bg-gray-800 border border-gray-700 rounded-lg px-3 py-1.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 w-48'
                placeholder={t('metrics.filterPlaceholder')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </CardHeader>
            <CardContent>
              {isLoading && (
                <div className='py-8 text-center text-sm text-gray-500'>{t('common.loading')}</div>
              )}
              {isError && (
                <div className='py-8 text-center text-sm text-red-400'>
                  {t('metrics.loadError')}
                </div>
              )}
              {!isLoading && !isError && nodes.length === 0 && (
                <EmptyState
                  icon={<Activity size={28} />}
                  title={t('metrics.noDataTitle')}
                  description={t('metrics.noDataDescription')}
                />
              )}
              <div className='space-y-2'>
                {nodes.map((n, i) => <NodeCard key={`${n.host}-${n.measurement}-${i}`} row={n} />)}
              </div>
            </CardContent>
          </Card>
        </TabPanel>

        <TabPanel id='grafana'>
          <GrafanaSetup />
        </TabPanel>
      </Tabs>
    </div>
  );
}
